/**
 * `studio/:moduleId/:tab` — the module the address names, at the tab it names.
 *
 * @module router/programme-editor/studio/ModuleEditorRoute
 */

import React from "react";

import { Navigate, useLocation, useParams, useSearchParams } from "react-router";

import type { CrumbFn } from "@/components/shell/RouteBreadcrumb";
import { isModuleTab } from "@/components/studio/module-editor-model";
import { ModuleEditor } from "@/components/studio/ModuleEditor";
import { useStudioModules } from "@/components/studio/StudioSurface";
import { BELOW_LG, useMediaQuery } from "@/components/ui";
import { useRefreshAfterSaves } from "@/hooks/useRefreshAfterSaves";
import { useProgramme } from "@/hooks/useStore";
import { useEditorBase } from "@/router/programme-editor/SurfaceLayout";
import { useWorkspaceOutletContext } from "@/router/workspace-context";
import { state } from "@/state/store";

/** The tab a module opens on when the address names none. */
export const DEFAULT_MODULE_TAB = "details";

export function ModuleEditorRoute(): React.ReactElement | null {
  const { programmeId, moduleId, tab, changeCaseId } = useParams<{
    programmeId: string;
    moduleId: string;
    tab?: string;
    changeCaseId?: string;
  }>();
  const base = useEditorBase();
  const { search } = useLocation();
  const {
    client,
    editLeasesEnabled,
    editorSync,
    refreshPlatformWorkspace,
    session,
    platformProgrammes,
    viewingProgramme,
  } = useWorkspaceOutletContext();
  const { programme } = useProgramme();
  // The programme store and the readiness both read module content, so a
  // module save has to reach them or the matrix and the flags lag. Neither
  // read waits on the other.
  const refreshAfterSave = useRefreshAfterSaves(() =>
    Promise.all([refreshPlatformWorkspace(), editorSync.reloadProgramme()]),
  );

  if (!programmeId || !moduleId) {
    return null;
  }
  const summary = platformProgrammes.find((candidate) => candidate.id === programmeId);
  if (!isModuleTab(tab)) {
    return <Navigate to={`${base}/studio/${moduleId}/${DEFAULT_MODULE_TAB}${search}`} replace />;
  }

  return (
    <ModuleEditor
      // A different module is a fresh mount, so no save, lease or conflict
      // state outlives the module it belonged to.
      key={`${programmeId}:${changeCaseId ?? ""}:${moduleId}`}
      client={client}
      editLeasesEnabled={editLeasesEnabled}
      programmeId={programmeId}
      moduleId={moduleId}
      changeCaseId={changeCaseId}
      tab={tab}
      tabBase={`${base}/studio/${moduleId}`}
      clientId={editorSync.clientId}
      readerUserId={session.subject.userId}
      programmeModules={programme.modules ?? []}
      programme={programme}
      leadName={summary?.leadUserName}
      readerLeads={Boolean(summary?.leadUserId) && summary?.leadUserId === session.subject.userId}
      onSaved={refreshAfterSave}
      viewOnly={viewingProgramme}
    />
  );
}

export default ModuleEditorRoute;

/**
 * The studio's own address, entered with no module named.
 *
 * Opens the first module rather than an empty pane: the surface exists to
 * edit one, and choosing for the reader is what every list-and-detail screen
 * in the app already does. Not below `lg`, where the list is the only pane
 * and "All modules" leads here.
 */
export function FirstModuleRedirect(): React.ReactElement | null {
  const base = useEditorBase();
  const [searchParams] = useSearchParams();
  const first = useStudioModules()[0]?.id;
  const listOnly = useMediaQuery(BELOW_LG);
  if (!first || listOnly) {
    return null;
  }
  // A legacy step that collapsed into this surface (e.g. `edit/effort-hours`)
  // names its owning tab in `?tab=`, so its bookmark still opens where it did.
  // Anything else in the query rides along to the module it opens.
  const rest = new URLSearchParams(searchParams);
  const requestedTab = rest.get("tab") ?? undefined;
  rest.delete("tab");
  const tab = isModuleTab(requestedTab) ? requestedTab : DEFAULT_MODULE_TAB;
  const query = rest.size > 0 ? `?${rest.toString()}` : "";
  return <Navigate to={`${base}/studio/${first}/${tab}${query}`} replace />;
}

/** The module's own title, for the breadcrumb trail below the surface. */
export const moduleCrumb: CrumbFn = (params) => {
  const found = (state.programme.modules ?? []).find((module) => module.id === params.moduleId);
  return found?.title || "Module";
};
